import React, { useState, useEffect, useRef } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { OverviewCards } from '../../components/analytics/OverviewCards';
import { PrecisionChart } from '../../components/analytics/PrecisionChart';
import { FieldsTable } from '../../components/analytics/FieldsTable';
import { ErrorTypesPie } from '../../components/analytics/ErrorTypesPie';
import { WorstOcrTable } from '../../components/analytics/WorstOcrTable';
import { ModelPerformanceTable } from '../../components/analytics/ModelPerformanceTable';
import { SuggestionsPanel } from '../../components/analytics/SuggestionsPanel';
import { analyticsService } from '../../services/analyticsService';
import { domainsService } from '../../services/domainsService';

export const AnalyticsPage: React.FC = () => {
  const [domains, setDomains] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [domainsLoaded, setDomainsLoaded] = useState(false);

  // Load active domains on mount
  useEffect(() => {
    const loadDomains = async () => {
      try {
        const domainOptions = await domainsService.getActiveDomainOptions();
        setDomains(domainOptions);
        if (domainOptions.length > 0 && !selectedDomain) {
          setSelectedDomain(domainOptions[0].value);
        }
      } catch (err) {
        console.error('Failed to load domains:', err);
      } finally {
        setDomainsLoaded(true);
      }
    };
    loadDomains();
  }, []);

  const { overview, fields, suggestions, worstOcr, modelPerf, isLoading, error, refetch } = useAnalytics(
    selectedDomain || undefined
  );

  // Auto-analyze on domain change (run once per domain selection)
  const analyzedDomainRef = useRef<string>('');

  useEffect(() => {
    if (!selectedDomain || selectedDomain === analyzedDomainRef.current) return;
    analyzedDomainRef.current = selectedDomain;

    let cancelled = false;
    const run = async () => {
      setIsAnalyzing(true);
      try {
        await analyticsService.analyzeAll(selectedDomain);
        if (!cancelled) refetch();
      } catch (err) {
        console.error('Analysis error:', err);
      } finally {
        if (!cancelled) setIsAnalyzing(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [selectedDomain]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!domainsLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (domains.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Aucun domaine actif configuré</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Analytics - Qualité d'extraction</h1>
        <div className="flex items-center gap-4">
          <select
            value={selectedDomain}
            onChange={(e) => setSelectedDomain(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          >
            {domains.map((d, index) => (
              <option key={`${d.value}-${index}`} value={d.value}>{d.label}</option>
            ))}
          </select>
          {isAnalyzing && (
            <span className="text-sm text-gray-500">Analyse en cours...</span>
          )}
        </div>
      </div>

      {(isLoading || isAnalyzing) && !overview ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Chargement des analytics...</div>
        </div>
      ) : overview ? (
        <div className="space-y-6">
          {/* Overview Cards */}
          <OverviewCards metrics={overview} />

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PrecisionChart data={overview.trend_30_days || []} />
            <ErrorTypesPie errorsByType={overview.errors_by_type || {}} />
          </div>

          {/* Fields Table */}
          {fields.length > 0 && (
            <FieldsTable fields={fields} />
          )}

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <WorstOcrTable pages={worstOcr} />
            <ModelPerformanceTable models={modelPerf} />
            <SuggestionsPanel suggestions={suggestions} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AnalyticsPage;
